import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { UserRow } from "./sql";
import { fmtDateShort } from "../../lib/funnel/helpers";
import { Td } from "../../lib/funnel/table";
import { downloadCsv } from "../../lib/funnel/csv";
import type { Filters } from "../../lib/funnel/filters";
import { distinctUserCount } from "./helpers";
import { SectionCardHeader } from "../../components/ChartCard";
import type { FilterPillSpec } from "../../components/FilterPills";

type SortKey =
  | "name"
  | "synopsis_count"
  | "chat_meeting_count"
  | "chat_client_count"
  | "custom_template_count"
  | "days_since_signup"
  | "days_to_first_synopsis"
  | "last_active_date"
  | "created_at_utc"
  | "first_demo_date"
  | "first_transcript_date"
  | "first_synopsis_date"
  | "first_calendar_date"
  | "first_chat_meeting_date"
  | "first_chat_client_date"
  | "first_custom_template_date"
  | "subscription_started_date_utc";
type SortDir = "asc" | "desc";

const PLAN_TONE: Record<string, string> = { free: "b-gray", plus: "b-blue", pro: "b-violet" };

type FilterableKey = "country" | "role" | "plan" | "path" | "cal" | "attr";

const FILTER_VALUE: Record<FilterableKey, (u: UserRow) => string> = {
  country: (u) => u.country_code ?? "",
  role: (u) => u.user_role ?? "",
  plan: (u) => u.billing_plan ?? "",
  path: (u) => (u.had_demo ? "demo" : "self"),
  cal: (u) => (u.calendar_connected ? "yes" : "no"),
  attr: (u) => u.attribution_category ?? "",
};

type Column = {
  key: string;
  label: string;
  sortKey?: SortKey;
  filterKey?: FilterableKey;
  defaultWidth: number;
  minWidth: number;
  align?: "left" | "right";
  tooltip: string;
  defaultVisible?: boolean;
  render: (u: UserRow) => ReactNode;
  cellClassName?: string;
};

const COLUMNS: Column[] = [
  // Identity
  { key: "name", label: "Name", sortKey: "name", defaultWidth: 110, minWidth: 50, tooltip: "User's full name, from first and last name on record.", cellClassName: "name-cell", render: (u) => [u.first_name, u.last_name].filter(Boolean).join(" ") || "" },
  { key: "email", label: "Email", defaultWidth: 160, minWidth: 60, tooltip: "Sign-up email address.", render: (u) => u.email },
  { key: "company", label: "Company", defaultWidth: 120, minWidth: 60, tooltip: "Workspace the user belongs to.", render: (u) => u.company_name ?? "" },
  { key: "country", label: "Country", filterKey: "country", defaultWidth: 70, minWidth: 50, tooltip: "Workspace country code from the companies table.", render: (u) => u.country_code ?? "" },
  { key: "plan", label: "Billing plan", filterKey: "plan", defaultWidth: 80, minWidth: 60, tooltip: "Current billing plan of the workspace: free, plus, or pro.", render: (u) => (
    u.billing_plan ? <span className={`badge ${PLAN_TONE[u.billing_plan] ?? "b-gray"}`}>{u.billing_plan}</span> : <span className="muted"></span>
  ) },
  { key: "path", label: "Sign-up path", filterKey: "path", defaultWidth: 90, minWidth: 60, tooltip: "Demo if the user had a sales demo, otherwise self-serve.", render: (u) => (
    u.had_demo
      ? <span className="badge b-blue"><i className="ti ti-presentation" />demo</span>
      : <span className="badge b-gray">self</span>
  ) },
  // First-event timestamps for each funnel stage
  { key: "first_demo_date", label: "First demo", sortKey: "first_demo_date", defaultWidth: 95, minWidth: 60, tooltip: "Earliest demo this user attended.", render: (u) => fmtDateShort(u.first_demo_date) },
  { key: "created_at_utc", label: "Signed up", sortKey: "created_at_utc", defaultWidth: 95, minWidth: 60, tooltip: "Sign-up date.", render: (u) => fmtDateShort(u.created_at_utc) },
  { key: "first_transcript_date", label: "First transcript", sortKey: "first_transcript_date", defaultWidth: 95, minWidth: 60, tooltip: "Date of the first non-demo transcript.", render: (u) => fmtDateShort(u.first_transcript_date) },
  { key: "first_synopsis_date", label: "First summary", sortKey: "first_synopsis_date", defaultWidth: 95, minWidth: 60, tooltip: "Date of the first non-demo summary.", render: (u) => fmtDateShort(u.first_synopsis_date) },
  { key: "first_calendar_date", label: "Connected calendar", sortKey: "first_calendar_date", defaultWidth: 95, minWidth: 60, tooltip: "Earliest calendar row for the user's integration (proxy for first connect).", render: (u) => fmtDateShort(u.first_calendar_date) },
  { key: "first_chat_meeting_date", label: "First Ask (meeting)", sortKey: "first_chat_meeting_date", defaultWidth: 95, minWidth: 60, tooltip: "Date of the first Ask Marloo chat keyed on a meeting or synopsis.", render: (u) => fmtDateShort(u.first_chat_meeting_date) },
  { key: "first_chat_client_date", label: "First Ask (client)", sortKey: "first_chat_client_date", defaultWidth: 95, minWidth: 60, tooltip: "Date of the first Ask Marloo chat keyed on a client. Strongest pre-paid predictor.", render: (u) => fmtDateShort(u.first_chat_client_date) },
  { key: "first_custom_template_date", label: "First custom template", sortKey: "first_custom_template_date", defaultWidth: 95, minWidth: 60, tooltip: "First user-created template (excludes auto-provisioned).", render: (u) => fmtDateShort(u.first_custom_template_date) },
  { key: "subscription_started_date_utc", label: "Converted to paid", sortKey: "subscription_started_date_utc", defaultWidth: 95, minWidth: 60, tooltip: "Subscription start date for the paid plan.", render: (u) => fmtDateShort(u.subscription_started_date_utc) },
  // Secondary
  { key: "role", label: "Role", filterKey: "role", defaultWidth: 80, minWidth: 60, tooltip: "User role within the company workspace.", render: (u) => u.user_role ?? "" },
  { key: "cal", label: "Calendar connected", filterKey: "cal", defaultWidth: 80, minWidth: 50, tooltip: "Whether the user has connected a calendar integration.", render: (u) => u.calendar_connected ? <i className="ti ti-check" /> : <span className="muted"></span> },
  { key: "age", label: "Days since sign-up", sortKey: "days_since_signup", defaultWidth: 80, minWidth: 50, align: "right", tooltip: "Days between sign-up date and today.", cellClassName: "num", render: (u) => u.days_since_signup },
  { key: "ttfs", label: "Days to first summary", sortKey: "days_to_first_synopsis", defaultWidth: 90, minWidth: 50, align: "right", tooltip: "Days between sign-up and the first summary. Blank if no summary yet.", cellClassName: "num", render: (u) => u.days_to_first_synopsis ?? "" },
  { key: "last", label: "Last active date", sortKey: "last_active_date", defaultWidth: 95, minWidth: 60, tooltip: "Most recent summary or transcript date.", render: (u) => fmtDateShort(u.last_active_date) },
  { key: "syns", label: "Summaries", sortKey: "synopsis_count", defaultWidth: 80, minWidth: 50, align: "right", tooltip: "Total meeting summaries generated by the user in this workspace.", cellClassName: "num", render: (u) => u.synopsis_count },
  { key: "chats_meeting", label: "Ask (meeting)", sortKey: "chat_meeting_count", defaultWidth: 80, minWidth: 50, align: "right", tooltip: "Ask Marloo chats keyed on a meeting/synopsis (this workspace).", cellClassName: "num", render: (u) => u.chat_meeting_count },
  { key: "chats_client", label: "Ask (client)", sortKey: "chat_client_count", defaultWidth: 80, minWidth: 50, align: "right", tooltip: "Ask Marloo chats keyed on a client (this workspace).", cellClassName: "num", render: (u) => u.chat_client_count },
  { key: "tpls", label: "Custom templates", sortKey: "custom_template_count", defaultWidth: 80, minWidth: 50, align: "right", tooltip: "User-created templates (excludes auto-provisioned at sign-up).", cellClassName: "num", render: (u) => u.custom_template_count },
  { key: "attr", label: "Attribution category", filterKey: "attr", defaultWidth: 110, minWidth: 60, tooltip: "Source bucket derived from the free-text attribution field at sign-up.", render: (u) => u.attribution_category },
  { key: "links", label: "External links", defaultWidth: 80, minWidth: 50, tooltip: "Deep links to LogRocket session replay (LR) and Mixpanel profile (MP).", render: (u) => (
    <span className="linklist">
      <a href={u.logrocket_link} target="_blank" rel="noreferrer" title="LogRocket">LR</a>
      <a href={u.mixpanel_link} target="_blank" rel="noreferrer" title="Mixpanel">MP</a>
    </span>
  ) },
];

const PAGE_SIZE = 100;

export function UserTable({
  users,
  highlightedUserId,
  onHighlightUser,
  filters,
  pagePills,
}: {
  users: UserRow[];
  highlightedUserId?: string | null;
  onHighlightUser?: (id: string | null) => void;
  filters?: Filters;
  pagePills?: FilterPillSpec[];
}) {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("synopsis_count");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(0);
  const [widths, setWidths] = useState<Record<string, number>>(() => {
    const w: Record<string, number> = {};
    COLUMNS.forEach((c) => { w[c.key] = c.defaultWidth; });
    return w;
  });
  const [colFilters, setColFilters] = useState<Partial<Record<FilterableKey, string[]>>>({});
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(() => new Set(COLUMNS.map((c) => c.key)));
  const [pickerOpen, setPickerOpen] = useState(false);
  const visibleColumns = useMemo(() => COLUMNS.filter((c) => visibleKeys.has(c.key)), [visibleKeys]);

  const dragRef = useRef<{ key: string; startX: number; startWidth: number } | null>(null);

  const filterOptions = useMemo(() => {
    const out: Partial<Record<FilterableKey, string[]>> = {};
    (Object.keys(FILTER_VALUE) as FilterableKey[]).forEach((k) => {
      const vals = new Set<string>();
      users.forEach((u) => {
        const v = FILTER_VALUE[k](u);
        if (v) vals.add(v);
      });
      out[k] = Array.from(vals).sort();
    });
    return out;
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const activeFilters = (Object.keys(colFilters) as FilterableKey[])
      .map((k) => {
        const vals = colFilters[k];
        return vals && vals.length > 0 ? { k, set: new Set(vals) } : null;
      })
      .filter((x): x is { k: FilterableKey; set: Set<string> } => x !== null);
    return users.filter((u) => {
      for (const { k, set } of activeFilters) {
        if (!set.has(FILTER_VALUE[k](u))) return false;
      }
      if (q) {
        const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.toLowerCase();
        if (!name.includes(q) && !u.email.toLowerCase().includes(q) && !(u.company_name ?? "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [users, search, colFilters]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      // Highlighted row pinned to top regardless of column sort.
      const aHi = highlightedUserId && a.external_id === highlightedUserId;
      const bHi = highlightedUserId && b.external_id === highlightedUserId;
      if (aHi && !bHi) return -1;
      if (bHi && !aHi) return 1;
      const va = sortVal(a, sortKey);
      const vb = sortVal(b, sortKey);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir, highlightedUserId]);

  // Reset to page 0 whenever a highlight is set so the user is visible.
  useEffect(() => {
    if (highlightedUserId) setPage(0);
  }, [highlightedUserId]);

  const paginated = useMemo(() => sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [sorted, page]);
  const pageCount = Math.ceil(sorted.length / PAGE_SIZE);

  const toggleSort = (key: SortKey) => {
    setPage(0);
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const startResize = (key: string, e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const col = COLUMNS.find((c) => c.key === key);
    dragRef.current = { key, startX: e.clientX, startWidth: widths[key] ?? col?.defaultWidth ?? 120 };
    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const c = COLUMNS.find((cc) => cc.key === d.key);
      const min = c?.minWidth ?? 60;
      const next = Math.max(min, d.startWidth + (ev.clientX - d.startX));
      setWidths((prev) => ({ ...prev, [d.key]: next }));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const tooltipText = COLUMNS.map((c) => `${c.label}: ${c.tooltip}`).join("\n");
  const distinctUsers = useMemo(() => distinctUserCount(sorted), [sorted]);

  const exportCsv = () => {
    const rows = sorted.map((u) => ({
      user_external_id: u.user_external_id,
      company_external_id: u.company_external_id,
      email: u.email,
      full_name: u.full_name,
      country_code: u.country_code,
      user_role: u.user_role,
      billing_plan: u.billing_plan,
      had_demo: u.had_demo,
      first_demo_date: u.first_demo_date,
      created_at_utc: u.created_at_utc,
      first_transcript_date: u.first_transcript_date,
      first_synopsis_date: u.first_synopsis_date,
      first_calendar_date: u.first_calendar_date,
      first_chat_meeting_date: u.first_chat_meeting_date,
      first_chat_client_date: u.first_chat_client_date,
      first_custom_template_date: u.first_custom_template_date,
      subscription_started_date_utc: u.subscription_started_date_utc,
      transcript_count: u.transcript_count,
      synopsis_count: u.synopsis_count,
      chat_meeting_count: u.chat_meeting_count,
      chat_client_count: u.chat_client_count,
      custom_template_count: u.custom_template_count,
      attribution_category: u.attribution_category,
      utm_source: u.user_utm_source,
      utm_campaign: u.user_utm_campaign,
    } as Record<string, unknown>));
    downloadCsv(
      rows,
      Object.keys(rows[0] ?? {}),
      "signup-funnel",
      filters ?? null,
    );
  };

  const highlightedUser = highlightedUserId
    ? users.find((u) => u.external_id === highlightedUserId)
    : null;
  const selectionLabel = highlightedUser
    ? (`${highlightedUser.first_name ?? ""} ${highlightedUser.last_name ?? ""}`.trim()
        ? `${`${highlightedUser.first_name ?? ""} ${highlightedUser.last_name ?? ""}`.trim()} (${highlightedUser.email})`
        : highlightedUser.email)
    : null;

  const pills: FilterPillSpec[] = [...(pagePills ?? [])];
  if (selectionLabel) {
    pills.unshift({
      kind: "selection",
      label: `Selected: ${selectionLabel}`,
      onClear: () => onHighlightUser?.(null),
    });
  }

  return (
    <section className="card">
      <SectionCardHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            User drilldown
            <i
              className="ti ti-info-circle"
              title={tooltipText}
              style={{ color: "var(--fg-3)", fontSize: 15, cursor: "help" }}
            />
          </span>
        }
        subtitle={`${distinctUsers.toLocaleString()} distinct users across ${sorted.length.toLocaleString()} user-workspace rows. Activity is attributed to the workspace it happened in, so a user active in two workspaces shows up twice.`}
        filterPills={pills}
        headerExtra={
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span className="input-wrap">
              <i className="ti ti-search" />
              <input
                type="text"
                className="input"
                placeholder="Search name, email, company"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              />
            </span>
            <div style={{ position: "relative" }}>
              <button type="button" className="btn btn-sm" onClick={() => setPickerOpen((v) => !v)}>
                <i className="ti ti-columns" /> Columns ({visibleKeys.size}/{COLUMNS.length})
              </button>
              {pickerOpen && (
                <UserTableColumnPicker
                  visibleKeys={visibleKeys}
                  onToggle={(key) => setVisibleKeys((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key); else next.add(key);
                    return next;
                  })}
                  onShowAll={() => setVisibleKeys(new Set(COLUMNS.map((c) => c.key)))}
                  onClose={() => setPickerOpen(false)}
                />
              )}
            </div>
            <button type="button" className="btn btn-sm" onClick={exportCsv}>
              <i className="ti ti-download" /> Download CSV
            </button>
          </div>
        }
      />
      <div className="table-wrap no-border-top">
        <table className="t t-resizable" style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
          <colgroup>
            {visibleColumns.map((c) => (
              <col key={c.key} style={{ width: `${widths[c.key] ?? c.defaultWidth}px` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {visibleColumns.map((c) => {
                const isSort = c.sortKey != null;
                const active = c.sortKey === sortKey;
                const arrow = active ? (sortDir === "asc" ? " ▲" : " ▼") : "";
                const fk = c.filterKey;
                const selected = fk ? colFilters[fk] ?? [] : [];
                return (
                  <th
                    key={c.key}
                    className={isSort ? "sortable" : undefined}
                    style={{ textAlign: c.align ?? "left", position: "relative" }}
                  >
                    <span
                      className={`th-label${isSort ? "" : " no-sort"}`}
                      onClick={isSort && c.sortKey ? () => toggleSort(c.sortKey!) : undefined}
                    >
                      {c.label}{arrow}
                    </span>
                    {fk && (
                      <ColFilter
                        label={c.label}
                        options={filterOptions[fk] ?? []}
                        selected={selected}
                        onChange={(next) => {
                          setPage(0);
                          setColFilters((prev) => ({ ...prev, [fk]: next }));
                        }}
                      />
                    )}
                    <span
                      className="col-resizer"
                      onMouseDown={(e) => startResize(c.key, e)}
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paginated.map((u) => {
              const isHi = highlightedUserId === u.external_id;
              return (
                <tr
                  key={u.external_id}
                  className={isHi ? "is-highlighted" : ""}
                  onClick={() => onHighlightUser?.(isHi ? null : u.external_id)}
                  style={{ cursor: onHighlightUser ? "pointer" : undefined }}
                >
                  {visibleColumns.map((c) => (
                    <Td key={c.key} className={c.cellClassName}>{c.render(u)}</Td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <div className="pagination-bar">
          <button className="btn btn-sm" onClick={() => setPage((p) => p - 1)} disabled={page === 0}>Previous</button>
          <span className="pagination-info">Page {page + 1} of {pageCount} ({sorted.length} rows)</span>
          <button className="btn btn-sm" onClick={() => setPage((p) => p + 1)} disabled={page >= pageCount - 1}>Next</button>
        </div>
      )}
    </section>
  );
}

function ColFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);
  const active = selected.length > 0;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (v: string, multi: boolean) => {
    if (multi) {
      onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
    } else {
      onChange(selected.length === 1 && selected[0] === v ? [] : [v]);
    }
  };

  return (
    <span
      ref={ref}
      style={{ position: "relative", display: "inline-flex", marginLeft: 4, verticalAlign: "middle" }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        aria-label={`Filter ${label}`}
        title={active ? `${label} filtered (${selected.length})` : `Filter ${label}`}
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "transparent",
          border: "none",
          padding: 2,
          cursor: "pointer",
          color: active ? "var(--color-teal)" : "var(--fg-3)",
          display: "inline-flex",
          alignItems: "center",
          fontSize: 13,
        }}
      >
        <i className={active ? "ti ti-filter-filled" : "ti ti-filter"} />
      </button>
      {open && (
        <div className="month-multi-popover" role="listbox" onClick={(e) => e.stopPropagation()}>
          <label className="row">
            <input
              type="checkbox"
              checked={selected.length === 0}
              onChange={() => onChange([])}
            />
            <span>All</span>
          </label>
          <div className="divider" />
          <span className="multi-hint">Shift+click to select multiple</span>
          {options.map((o) => (
            <label key={o} className="row" onClick={(e) => { e.preventDefault(); toggle(o, e.shiftKey); }}>
              <input type="checkbox" checked={selected.includes(o)} onChange={() => {}} tabIndex={-1} />
              <span>{o}</span>
            </label>
          ))}
        </div>
      )}
    </span>
  );
}

function UserTableColumnPicker({
  visibleKeys,
  onToggle,
  onShowAll,
  onClose,
}: {
  visibleKeys: Set<string>;
  onToggle: (key: string) => void;
  onShowAll: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [onClose]);
  return (
    <div
      ref={ref}
      className="month-multi-popover"
      role="listbox"
      style={{ right: 0, left: "auto", maxHeight: 360, overflowY: "auto" }}
      onClick={(e) => e.stopPropagation()}
    >
      <label className="row" onClick={(e) => { e.preventDefault(); onShowAll(); }}>
        <input type="checkbox" checked={visibleKeys.size === COLUMNS.length} onChange={() => {}} tabIndex={-1} />
        <span>Show all</span>
      </label>
      <div className="divider" />
      {COLUMNS.map((c) => (
        <label key={c.key} className="row" onClick={(e) => { e.preventDefault(); onToggle(c.key); }}>
          <input type="checkbox" checked={visibleKeys.has(c.key)} onChange={() => {}} tabIndex={-1} />
          <span>{c.label}</span>
        </label>
      ))}
    </div>
  );
}

function sortVal(u: UserRow, k: SortKey): string | number | null {
  switch (k) {
    case "name": return `${u.first_name ?? ""} ${u.last_name ?? ""}`.toLowerCase();
    case "synopsis_count": return u.synopsis_count;
    case "chat_meeting_count": return u.chat_meeting_count;
    case "chat_client_count": return u.chat_client_count;
    case "custom_template_count": return u.custom_template_count;
    case "days_since_signup": return u.days_since_signup;
    case "days_to_first_synopsis": return u.days_to_first_synopsis;
    case "last_active_date": return u.last_active_date;
    case "created_at_utc": return u.created_at_utc;
    case "first_demo_date": return u.first_demo_date;
    case "first_transcript_date": return u.first_transcript_date;
    case "first_synopsis_date": return u.first_synopsis_date;
    case "first_calendar_date": return u.first_calendar_date;
    case "first_chat_meeting_date": return u.first_chat_meeting_date;
    case "first_chat_client_date": return u.first_chat_client_date;
    case "first_custom_template_date": return u.first_custom_template_date;
    case "subscription_started_date_utc": return u.subscription_started_date_utc;
  }
}
